import {
  type AnimationOptions,
  type AnimationSequence,
  animate,
  type DOMKeyframesDefinition,
} from "motion"
import {
  createSignal,
  onCleanup,
  onMount,
  type ParentComponent,
} from "solid-js"

interface AnimatePresenceProps {
  from?: DOMKeyframesDefinition
  to?: DOMKeyframesDefinition
  exit?: DOMKeyframesDefinition
  enterOptions?: AnimationOptions
  exitOptions?: AnimationOptions
  keepInitialPosition?: boolean
  initial?: boolean
}

interface SnapshotPosition {
  top: number
  left: number
  width: number
  height: number
}

/**
 * Captures the current position and dimensions of an element.
 * Returns viewport-relative coordinates suitable for absolute positioning on body.
 */
function snapshotPosition(element: HTMLElement): SnapshotPosition {
  const rect = element.getBoundingClientRect()
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  }
}

/**
 * A component that animates its children on mount and unmount.
 * On unmount, it clones the element and animates it out, allowing for smooth exit animations
 * even when the original element is removed from the DOM.
 *
 * @param initial - If false, skips the initial enter animation on mount. Defaults to true.
 */
export const AnimatePresence: ParentComponent<AnimatePresenceProps> = props => {
  const [ref, setRef] = createSignal<HTMLDivElement>()
  let initialPosition: SnapshotPosition | undefined

  onMount(() => {
    const element = ref()
    if (element) {
      const shouldAnimate = props.initial !== false

      if (shouldAnimate) {
        animate([
          [element, props.from || { opacity: 0 }, { duration: 0 }],
          [
            element,
            props.to || { opacity: 1 },
            { duration: 0.3, ...props.enterOptions },
          ],
        ] satisfies AnimationSequence).then(() => {
          if (props.keepInitialPosition) {
            initialPosition = snapshotPosition(element)
          }
        })
      } else {
        animate([
          [element, props.to || { opacity: 1 }, { duration: 0 }],
        ] satisfies AnimationSequence).then(() => {
          if (props.keepInitialPosition) {
            initialPosition = snapshotPosition(element)
          }
        })
      }
    }
  })

  onCleanup(() => {
    const element = ref()
    const parentElement = element?.parentElement
    if (element && parentElement) {
      // Use initial position if keepInitialPosition is enabled, otherwise get current position
      const position =
        props.keepInitialPosition && initialPosition
          ? initialPosition
          : snapshotPosition(element)

      // Clone the element
      const clone = element.cloneNode(true) as HTMLElement

      // Position the clone absolutely at the same position
      clone.style.position = "absolute"
      clone.style.top = `${position.top}px`
      clone.style.left = `${position.left}px`
      clone.style.width = `${position.width}px`
      clone.style.height = `${position.height}px`
      clone.style.pointerEvents = "none"

      // Append to document body to maintain position
      document.body.appendChild(clone)

      // Animate the clone and remove it after animation
      animate(clone, props.exit || props.from || { opacity: 0 }, {
        duration: 0.1,
        ...props.exitOptions,
      }).finished.then(() => {
        clone.remove()
      })
    }
  })

  return <div ref={setRef}>{props.children}</div>
}
